import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormGroup,
  FormsModule,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormControl,
  FormArray
} from '@angular/forms'
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import {
  PlanResponse,
  PlansAnalytics,
  PlansListResponse,
  PlansService
} from '../../services/plans.service'
import { selectAllCountries } from '../../store/country/country.selectors'
import { Store } from '@ngrx/store'
import { CommonModule, NgFor, NgIf } from '@angular/common'
import { loadCountries } from '../../store/country/country.actions'
import { ToastrService } from 'ngx-toastr'
import { debounceTime, distinctUntilChanged, map, Observable, Subject } from 'rxjs'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { RoleService } from '../../services/role.service'
import { FormatModuleNamePipe } from '../../pipes/format-module-name.pipe'

export interface PlanModule {
  id: string
  name: string

  dependencies?: PlanModule[]

  selected?: boolean
  autoAdded?: boolean
  mandatory?: boolean
}
@Component({
  selector: 'app-plans',
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbTooltipModule,
    PaginationComponent,
    FormatModuleNamePipe
  ],
  templateUrl: './plans.component.html',
  styleUrl: './plans.component.scss'
})
export class PlansComponent implements OnInit {
  store = inject(Store)
  modalService = inject(NgbModal)
  fb = inject(FormBuilder)
  form!: FormGroup
  @ViewChild('addPlanModal') addPlanModal!: TemplateRef<any>
  @ViewChild('deleteModal') deleteModal!: TemplateRef<any>
  toastr = inject(ToastrService)
  plansService = inject(PlansService)
  plans: PlanResponse[] = []
  analytics = { totalPlans: 0, activePlans: 0, inactivePlans: 0, noOfPlanTypes: 0 }
  operation = ''
  selectedPlan: PlanResponse | null = null
  selectedPlanType: 'monthly' | 'yearly' | null = null
  selectedPlanStatus: 'active' | 'inactive' | null = null
  currentPage = 1
  limit = 10
  search = ''
  countries$ = this.store.select(selectAllCountries)
  private searchSubject = new Subject<string>()

  totalCount = 0
  pageLastRecord = 0

  takenPositions: { monthly: number[]; yearly: number[] } = { monthly: [], yearly: [] }
  availablePositions: number[] = []
  // Available Features Management
  allPossibleFeatures = [
    { id: 'clock_in_out', name: 'Clock In/Out' },
    { id: 'permission_sets', name: 'Permission Sets' },
    { id: 'holidays', name: 'Holidays' },
    { id: 'leaves', name: 'Leaves' },
    { id: 'payrolls', name: 'Payrolls' },
    { id: 'salary', name: 'Salary' },
    { id: 'attendance_policy', name: 'Attendance Policy' },
    { id: 'payslips', name: 'Payslips' },
    { id: 'requests', name: 'Requests' }
  ]
  moduleService = inject(RoleService)
  modules: PlanModule[] = []
  modulesWithDeps: any[] = []
  modulesWithoutDeps: any[] = []
  constructor() {
    this.moduleService.modulesWithDependency().subscribe(res => {
      // this.modules = res.modules
      this.modules = res.modules.map((m: any) => ({
        ...m,
        selected: false,
        mandatory: m.name === 'employees' // employees mandatory
      }))
      const employeesModule = this.modules.find(m => m.name === 'employees')

      if (employeesModule) {
        employeesModule.selected = true
        employeesModule.mandatory = true
        this.selectDependencies(employeesModule)
      }
    })
  }
  ngOnInit() {
    this.store.dispatch(loadCountries())
    this.initializeForm()
    this.getPlans()
    this.getAnalytics()

    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.onFilterChange(true)
    })
  }

  initializeForm() {
    this.form = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      type: ['yearly', Validators.required],
      position: [null, Validators.required],
      minimumLicences: [1, [Validators.required, Validators.min(1)]],
      price: [0, [Validators.required, Validators.min(0)]],
      currencyId: [5, Validators.required],
      discountType: ['percentage', Validators.required],
      discountValue: [0, [Validators.required, Validators.min(0)]],
      // availableFeatures: this.fb.array(this.allPossibleFeatures.map(() => this.fb.control(false))),
      trialDays: [0, [Validators.required, Validators.min(0)]],
      isRecommended: [false, Validators.required],
      isActive: [true, Validators.required],
      description: ['']
    })
    // Listen for changes in the plan type dropdown
    this.form.get('type')?.valueChanges.subscribe(() => {
      this.updateAvailablePositions(this.selectedPlan)
      // Reset position when type changes to avoid invalid position selection
      this.form.get('position')?.setValue(null)
    })
  }

  get featuresFormArray(): FormArray {
    return this.form.get('availableFeatures') as FormArray
  }

  getAnalytics() {
    this.plansService.getAnalytics().subscribe({
      next: (response: PlansAnalytics) => {
        this.analytics = response
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error fetching analytics: ' + error.message)
      }
    })
  }

  getPlans() {
    const filters = {
      page: this.currentPage,
      limit: this.limit,
      type: this.selectedPlanType,
      status: this.selectedPlanStatus,
      search: this.search.trim()
    }

    this.plansService.getPlans(filters).subscribe({
      next: (response: PlansListResponse) => {
        this.totalCount = response.totalCount
        this.plans = response.plans
        this.takenPositions = response.takenPositions
        this.updateAvailablePositions()
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error fetching plans: ' + error.message)
      }
    })
  }

  updateAvailablePositions(editingPlan: PlanResponse | null = null) {
    const planType = this.form.get('type')?.value as 'monthly' | 'yearly'
    if (!planType) {
      this.availablePositions = []
      return
    }

    const currentTakenPositions = this.takenPositions[planType]
    const maxPosition = currentTakenPositions.length > 0 ? Math.max(...currentTakenPositions) : 0
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const potentialPositions = Array.from({ length: maxPosition + 1 }, (_, i) => i + 1)
    this.availablePositions = potentialPositions.filter(p => !currentTakenPositions.includes(p))

    // If editing a plan, its current position should be available,
    // but only if its type matches the selected type.
    if (editingPlan && editingPlan.type === planType) {
      if (!this.availablePositions.includes(editingPlan.position)) {
        this.availablePositions.push(editingPlan.position)
        this.availablePositions.sort((a, b) => a - b)
      }
    }
  }

  openModal(modal: unknown) {
    this.operation = 'Add'
    this.selectedPlan = null
    this.initializeForm()
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
  }

  // openEditModal(plan: PlanResponse, modal: TemplateRef<any>) {
  //   this.operation = 'Edit'
  //   this.selectedPlan = plan
  //   this.initializeForm()
  //   this.form.patchValue(plan)

  //   const featureValues = this.allPossibleFeatures.map(
  //     feature => plan.availableFeatures?.includes(feature.id) ?? false
  //   )

  //   // Manually set checkbox values for FormArray
  //   //this.featuresFormArray.controls.forEach((control, i) => {
  //   //  const featureId = this.allPossibleFeatures[i].id
  //   //  if (plan.availableFeatures?.includes(featureId)) {
  //   //    control.setValue(true)
  //   //  }
  //   //})
  //   this.featuresFormArray.setValue(featureValues)
  //   this.modalService.open(modal, {
  //     scrollable: false,
  //     centered: true,
  //     size: 'lg'
  //   })
  // }

  //10 march 2026
  openEditModal(plan: any, modal: TemplateRef<any>) {
    this.operation = 'Edit'
    this.selectedPlan = plan

    this.initializeForm()

    this.form.patchValue(plan)

    // reset modules
    this.modules.forEach(m => {
      m.selected = false
      m.autoAdded = false
    })

    // selected module ids from response
    const selectedModuleIds = plan.modules?.map((m: any) => m.moduleId) || []

    // mark selected modules
    this.modules.forEach(module => {
      if (selectedModuleIds.includes(module.id)) {
        module.selected = true
      }

      if (module.mandatory) {
        module.selected = true
        module.autoAdded = true
      }
    })

    // apply dependencies
    selectedModuleIds.forEach((id: string) => {
      const module = this.modules.find(m => m.id === id)

      if (module) {
        this.selectDependencies(module)
      }
    })

    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'lg'
    })
  }

  openDeleteModal(plan: PlanResponse, modal: TemplateRef<any>) {
    this.selectedPlan = plan
    this.modalService.open(modal, { centered: true })
  }

  closeModal() {
    this.operation = ''
    this.selectedPlan = null
    this.modalService.dismissAll()
  }

  // upsertPlan() {
  //   // Convert the boolean array of features back to an array of feature ID strings
  //   const selectedFeatureIds = this.form.value.availableFeatures
  //     .map((checked: boolean, i: number) => (checked ? this.allPossibleFeatures[i].id : null))
  //     .filter((value: string | null) => value !== null)

  //   const finalFormValue = { ...this.form.value, availableFeatures: selectedFeatureIds }
  //   const { id, ...planData } = finalFormValue
  //   if (this.operation === 'Add') {
  //     this.plansService.addPlan(planData).subscribe({
  //       next: () => {
  //         this.toastr.success('Plan created successfully!')
  //         this.getPlans()
  //         this.getAnalytics()
  //       },
  //       error: (error: { message: string }) => {
  //         this.toastr.error('Error: ' + error.message)
  //       }
  //     })
  //   } else {
  //     this.plansService.editPlan(id, planData).subscribe({
  //       next: () => {
  //         this.toastr.success('Plan updated successfully!')
  //         this.getPlans()
  //         this.getAnalytics()
  //       },
  //       error: (error: { message: string }) => {
  //         this.toastr.error('Error: ' + error.message)
  //       }
  //     })
  //   }
  //   this.closeModal()
  // }

  //gps 10 march 2026
  upsertPlan() {
    // selected modules
    // const selectedModules = this.modules.filter(m => m.selected).map(m => m.id)
    const selectedModules = this.getAllSelectedModules()

    const formValue = this.form.value

    const finalPayload = {
      ...formValue,
      modules: selectedModules
    }

    const { id, ...planData } = finalPayload

    if (this.operation === 'Add') {
      this.plansService.addPlan(planData).subscribe({
        next: () => {
          this.toastr.success('Plan created successfully!')
          this.getPlans()
          this.getAnalytics()
        },
        error: (error: any) => {
          this.toastr.error('Error: ' + error.message)
        }
      })
    } else {
      this.plansService.editPlan(id, planData).subscribe({
        next: () => {
          this.toastr.success('Plan updated successfully!')
          this.getPlans()
          this.getAnalytics()
        },
        error: (error: any) => {
          this.toastr.error('Error: ' + error.message)
        }
      })
    }

    this.closeModal()
  }

  confirmDelete() {
    if (!this.selectedPlan) {
      this.toastr.error('No plan selected for deletion.')
      this.closeModal()
      return
    }
    this.plansService.deletePlan(this.selectedPlan.id).subscribe({
      next: () => {
        this.toastr.success('Plan deleted successfully!')
        this.getPlans()
        this.getAnalytics()
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error: ' + error.message)
      }
    })
    this.closeModal()
  }

  onFilterChange(resetPage = false) {
    if (resetPage) this.currentPage = 1
    this.getPlans()
  }

  onSearchChange(searchValue: string) {
    this.searchSubject.next(searchValue)
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.getPlans() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.limit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.getPlans() // Load employees with new page limit
  }

  getModule(id: string) {
    return this.modules.find(m => m.id === id)
  }
  onCheckboxChange(module: PlanModule, event: Event) {
    const checked = (event.target as HTMLInputElement).checked

    this.toggleModule(module, checked)
  }
  // toggleModule(module: PlanModule, checked: boolean) {
  //   if (module.mandatory) return // ❌ employees cannot be unchecked

  //   if (checked) {
  //     module.selected = true
  //     this.selectDependencies(module)
  //   } else {
  //     module.selected = false
  //     this.unselectDependencies(module)
  //   }
  // }

  selectDependencies(module: PlanModule, visited = new Set<string>()) {
    if (!module.dependencies) return
    // console.log('module', module)
    // prevent circular recursion
    if (visited.has(module.id)) return
    visited.add(module.id)

    module.dependencies.forEach(dep => {
      dep.selected = true
      dep.autoAdded = true
      const dependencyModule = this.modules.find(m => m.id === dep.id)

      if (!dependencyModule) return

      if (!dependencyModule.selected) {
        dependencyModule.selected = true
        dependencyModule.autoAdded = true
      }

      // recursive call with visited
      this.selectDependencies(dependencyModule, visited)
    })
  }

  unselectDependencies(module: PlanModule, visited = new Set<string>()) {
    if (!module.dependencies) return

    if (visited.has(module.id)) return
    visited.add(module.id)

    module.dependencies.forEach(dep => {
      dep.selected = false
      dep.autoAdded = false
      const dependencyModule = this.modules.find(m => m.id === dep.id)

      if (!dependencyModule) return
      if (dependencyModule.mandatory) return

      const stillRequired = this.modules.some(
        m => m.selected && m.dependencies?.some(d => d.id === dependencyModule.id)
      )

      if (!stillRequired) {
        dependencyModule.selected = false
        dependencyModule.autoAdded = false

        this.unselectDependencies(dependencyModule, visited)
      }
    })
  }

  loadModules(res: any) {
    const modules = res.modules

    this.modulesWithDeps = modules.filter((m: any) => m.dependencies?.length)

    this.modulesWithoutDeps = modules.filter((m: any) => !m.dependencies?.length)
  }

  // toggleModule(module: PlanModule, checked: boolean) {
  //   if (module.mandatory) return

  //   if (checked) {
  //     module.selected = true

  //     this.selectDependencies(module)
  //     this.selectParentModules(module)
  //   } else {
  //     module.selected = false

  //     this.unselectDependencies(module)
  //     this.unselectParentModules(module)
  //   }
  // }
  toggleModule(module: PlanModule, checked: boolean) {
    if (module.mandatory) return

    if (checked) {
      module.selected = true
      this.selectDependencies(module) // ✅ sirf child dependencies select karo
      // ❌ parent ko select mat karo
    } else {
      module.selected = false
      this.unselectDependencies(module) // ✅ sirf child dependencies unselect karo
      // ❌ parent ko unselect mat karo
    }
  }

  getParentModules(moduleId: string): PlanModule[] {
    return this.modules.filter(m => m.dependencies?.some(d => d.id === moduleId))
  }

  selectParentModules(module: PlanModule, visited = new Set<string>()) {
    if (visited.has(module.id)) return
    visited.add(module.id)

    const parents = this.getParentModules(module.id)

    parents.forEach(parent => {
      if (!parent.selected) {
        parent.selected = true
        parent.autoAdded = true
      }

      // parent ki dependencies bhi select karo
      this.selectDependencies(parent, visited)

      // parent ke parent bhi check karo
      this.selectParentModules(parent, visited)
    })
  }

  unselectParentModules(module: PlanModule, visited = new Set<string>()) {
    if (visited.has(module.id)) return
    visited.add(module.id)

    const parents = this.getParentModules(module.id)

    parents.forEach(parent => {
      if (parent.mandatory) return

      const stillRequired = this.modules.some(
        m => m.selected && m.dependencies?.some(d => d.id === parent.id)
      )

      if (!stillRequired) {
        parent.selected = false
        parent.autoAdded = false

        // parent ki dependencies bhi unselect karo
        this.unselectDependencies(parent, visited)

        // parent ke parent bhi check karo
        this.unselectParentModules(parent, visited)
      }
    })
  }

  getAllSelectedModules(): string[] {
    const result = new Set<string>()
    this.modules.forEach(module => {
      if (module.selected) {
        result.add(module.id)

        // add dependencies
        this.collectDependencies(module, result)
      }
    })

    return Array.from(result)
  }

  collectDependencies(module: PlanModule, result: Set<string>) {
    if (!module.dependencies) return

    module.dependencies.forEach(dep => {
      result.add(dep.id)

      const fullDep = this.modules.find(m => m.id === dep.id)

      if (fullDep) {
        this.collectDependencies(fullDep, result)
      }
    })
  }
}
